import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useErrors } from 'Components/Organisms/ErrorsProvider';
import { getTransactionFiles, uploadTransactionFiles, deleteTransactionFile } from 'Services/transactions';
import { trackError } from 'Utils/errorMonitoring';
import { getRandomUUID } from 'Utils/strings';

export default function useTransactionFiles(id) {
    const { showUnknownError } = useErrors();
    const queryClient = useQueryClient();

    const { data: files = [], isLoading } = useQuery({
        queryKey: ['transactions', id, 'files'],
        queryFn: async () => getTransactionFiles(id),
        onError: (error) => trackError(error),
    });

    const { mutate: uploadFiles, isLoading: isUploadingFiles } = useMutation({
        mutationFn: (files) => uploadTransactionFiles(id, files),
        onMutate: async (files) => {
            await queryClient.cancelQueries({ queryKey: ['transactions', id, 'files'] });
            const previousFiles = queryClient.getQueryData(['transactions', id, 'files']);

            const placeHolders = Array.from(files).map((file) => ({
                id: getRandomUUID(),
                name: file.name,
                size: file.size,
                type: file.type,
                creation_date: new Date(),
                isPlaceholder: true,
            }));

            queryClient.setQueryData(['transactions', id, 'files'], (oldFiles) => [...oldFiles, ...placeHolders]);
            return { previousFiles };
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['transactions', id, 'files'] });
            await queryClient.invalidateQueries({ queryKey: ['transactions'] });
        },
        onError: (error, _variables, context) => {
            queryClient.setQueryData(['transactions', id, 'files'], context.previousFiles);
            showUnknownError(error);
        },
    });

    const { mutate: deleteFile, isLoading: isDeletingFile } = useMutation({
        mutationFn: (fileId) => deleteTransactionFile(id, fileId),
        onMutate: async (fileId) => {
            await queryClient.cancelQueries({ queryKey: ['transactions', id, 'files'] });
            const previousFiles = queryClient.getQueryData(['transactions', id, 'files']);

            queryClient.setQueryData(['transactions', id, 'files'], (oldFiles) =>
                oldFiles.filter((f) => f.id !== fileId),
            );

            return { previousFiles };
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['transactions', id, 'files'] });
            await queryClient.invalidateQueries({ queryKey: ['transactions'] });
        },
        onError: (error, _variables, context) => {
            queryClient.setQueryData(['transactions', id, 'files'], context.previousFiles);
            showUnknownError(error);
        },
    });

    return {
        files,
        isLoading,
        uploadFiles,
        deleteFile,
        isUpdating: isUploadingFiles || isDeletingFile
    };
}
